'use client';

import { c as _c } from "react/compiler-runtime";
import type { FieldsConfig } from '@effektify/types/definitions';
import type React from 'react';
import type { DateRangeValue } from 'src/components/Atoms/InputDateRangePicker';
import { FieldData, useDataProvider } from '@effektify/data-provider';
import { isFieldsConfig, isDateRange, isSelect } from '@effektify/types/guards';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { InputDateRangePicker } from 'src/components/Atoms/InputDateRangePicker';
import { InputSelect } from 'src/components/Atoms/InputSelect';
import { styled } from '@mui/material/styles';
import { Theme } from 'src/Theme';
dayjs.extend(utc);
const FormWrapper = styled(FlexRow)(({
  theme
}) => ({
  alignItems: 'center',
  gap: theme.sizes.spacing['06'],
  flexWrap: 'wrap',
  justifyContent: 'flex-end'
}));
const StyledButton = styled(Button)(({
  theme
}: {
  theme: Theme;
}) => ({
  width: 'fit-content',
  height: theme.fonts.lineHeight['leading-10'],
  padding: `${theme.sizes.spacing['05']} ${theme.sizes.spacing['08']}`,
  alignSelf: 'flex-end',
  bottom: theme.sizes.spacing.none,
  flex: '1 0 auto'
}));
const createDateRangeHandler = (startName: string, endName: string, onValueChange: (key: string, value: string | number | boolean) => void, onErrorChange: (key: string, hasError: boolean) => void, relativeTo: [dayjs.Dayjs | null, dayjs.Dayjs | null] | undefined) => ([start, end]: DateRangeValue) => {
  if (relativeTo && relativeTo[0] !== null && relativeTo[1] !== null && start === null && end === null) {
    onValueChange(startName, relativeTo[0].format('YYYY-MM-DD'));
    onValueChange(endName, relativeTo[1].format('YYYY-MM-DD'));
    onValueChange('no_comparison', true);
    onErrorChange(startName, false);
    onErrorChange(endName, false);
    return;
  }
  const isValidStart = start?.isValid() && start > dayjs.utc('1990-01-01') && start < dayjs.utc().endOf('year').add(1, 'day');
  const isValidEnd = end?.isValid() && end > dayjs.utc('1990-01-01') && end < dayjs.utc().endOf('year').add(1, 'day');
  onErrorChange(startName, !isValidStart);
  onErrorChange(endName, !isValidEnd);
  onValueChange(startName, isValidStart ? start!.format('YYYY-MM-DD') : '');
  onValueChange(endName, isValidEnd ? end!.format('YYYY-MM-DD') : '');
  onValueChange('no_comparison', false);
};
const ReportForm: React.FC<{
  defaultFormdata?: FieldData;
}> = t0 => {
  const $ = _c(32);
  const {
    defaultFormdata: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const defaultFormdata = t2;
  const [fields, setFields] = useState(null);
  const [fieldsData, setFieldsData] = useState(defaultFormdata);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [errors, setErrors] = useState(t3);
  const {
    handleSetParams,
    formData,
    form: fieldsConfig,
    isLoading
  } = useDataProvider();
  let t4;
  let t5;
  if ($[3] !== defaultFormdata || $[4] !== handleSetParams) {
    t4 = () => {
      if (defaultFormdata && Object.keys(defaultFormdata).length > 0) {
        handleSetParams(defaultFormdata);
      }
    };
    t5 = [handleSetParams, defaultFormdata];
    $[3] = defaultFormdata;
    $[4] = handleSetParams;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== fieldsData || $[8] !== handleSetParams) {
    t6 = () => {
      if (!fieldsData || Object.keys(fieldsData).length === 0) {
        return;
      }
      handleSetParams(fieldsData);
    };
    $[7] = fieldsData;
    $[8] = handleSetParams;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onSubmitForm = t6;
  let t7;
  let t8;
  if ($[10] !== fieldsConfig || $[11] !== isLoading) {
    t7 = () => {
      if (!isLoading && fieldsConfig && !isFieldsConfig(fieldsConfig)) {
        console.warn("Invalid form fields configuration");
        return;
      }
      if (!isLoading && fieldsConfig && isFieldsConfig(fieldsConfig)) {
        setFields(fieldsConfig);
      }
    };
    t8 = [fieldsConfig, isLoading, setFields];
    $[10] = fieldsConfig;
    $[11] = isLoading;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== formData) {
    t9 = () => {
      if (formData && Object.keys(formData).length > 0) {
        setFieldsData(prev => {
          if (prev && JSON.stringify(prev) === JSON.stringify(formData)) {
            return prev;
          }
          return {
            ...prev,
            ...formData
          };
        });
      }
    };
    t10 = [formData, setFieldsData];
    $[14] = formData;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  if ($[17] !== fields) {
    t11 = (key, value) => {
      if (!fields) {
        return;
      }
      setFieldsData(prev_0 => {
        if (prev_0[key] === value) {
          return prev_0;
        }
        return {
          ...prev_0,
          [key]: value
        };
      });
    };
    $[17] = fields;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const onValueChange = t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (key_0, hasError) => {
      setErrors(prev_1 => {
        const newErrors = new Set(prev_1);
        if (hasError) {
          newErrors.add(key_0);
        } else {
          newErrors.delete(key_0);
        }
        return newErrors;
      });
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const onErrorChange = t12;
  if (isLoading || !fields) {
    return null;
  }
  let t13;
  if ($[20] !== fields || $[21] !== fieldsData || $[22] !== onValueChange) {
    t13 = fields && Object.entries(fields).map(t14 => {
      const [key_1, field] = t14;
      if (field && isDateRange(field)) {
        const {
          start,
          end
        } = field.props;
        const date_start = fieldsData && fieldsData[start.name] ? fieldsData[start.name] as string : start.default || "";
        const date_end = fieldsData && fieldsData[end.name] ? fieldsData[end.name] as string : end.default || "";
        let relativeTo;
        let noComparison = false;
        if (field.relative_to) {
          const relativeToField = fields[field.relative_to];
          if (relativeToField && isDateRange(relativeToField)) {
            const relativeStart = fieldsData && fieldsData[relativeToField.props.start.name] ? fieldsData[relativeToField.props.start.name] as string : relativeToField.props.start.default || "";
            const relativeEnd = fieldsData && fieldsData[relativeToField.props.end.name] ? fieldsData[relativeToField.props.end.name] as string : relativeToField.props.end.default || "";
            relativeTo = [dayjs.utc(relativeStart), dayjs.utc(relativeEnd)];
            if (relativeStart === date_start && relativeEnd === date_end) {
              noComparison = true;
            }
          }
        }
        const onChange = createDateRangeHandler(start.name, end.name, onValueChange, onErrorChange, relativeTo);
        return <FlexColumn key={key_1} sx={{
          flex: 5
        }}><InputDateRangePicker key={key_1} label={field.label || ""} name={key_1} dateRange={noComparison ? [null, null] : [dayjs.utc(date_start), dayjs.utc(date_end)]} onChange={onChange} relativeTo={relativeTo} /></FlexColumn>;
      }
      if (field && isSelect(field)) {
        const value_0 = fieldsData && fieldsData[key_1] ? fieldsData[key_1] as string : field.default || field.fallback || "";
        return <FlexColumn key={key_1} sx={{
          flex: 5
        }}><InputSelect key={key_1} label={field.label || ""} name={key_1} value={value_0} onChange={value_1 => onValueChange(key_1, value_1)} options={field.options.map(_temp)} required={field.required || false} /></FlexColumn>;
      }
      return null;
    });
    $[20] = fields;
    $[21] = fieldsData;
    $[22] = onValueChange;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== onSubmitForm) {
    t14 = () => onSubmitForm();
    $[24] = onSubmitForm;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = errors.size > 0;
  let t16;
  if ($[26] !== t14 || $[27] !== t15) {
    t16 = <StyledButton onClick={t14} variant="contained" disabled={t15}>View</StyledButton>;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t13 || $[30] !== t16) {
    t17 = <FormWrapper>{t13}{t16}</FormWrapper>;
    $[29] = t13;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  return t17;
};
export default ReportForm;
function _temp(opt) {
  return {
    value: opt.value,
    text: opt.label
  };
}